import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PackageContext } from "./PackageContext";
import { EffectiveEntitlementResponse, FamilySummary, Package } from "../helper/interface";
import { get } from "../helper/APIClient";
import { Routes } from "../helper/Routes";
import { useProfile } from "./ProfileContext";

const ACTIVE_FAMILY_STORAGE_KEY = "activeFamilyId";

const readStoredActiveFamilyId = (): string | null | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const storedFamilyId = window.sessionStorage.getItem(ACTIVE_FAMILY_STORAGE_KEY);
  if (storedFamilyId === null) {
    return undefined;
  }

  const trimmedFamilyId = storedFamilyId.trim();
  return trimmedFamilyId === "" ? null : trimmedFamilyId;
};

interface Props {
  children: ReactNode;
}

export const PackageProvider = ({ children }: Props) => {
  const { profile } = useProfile();
  const [packages, setPackages] = useState<Package[]>([]);
  const [families, setFamilies] = useState<FamilySummary[]>([]);
  const [personalPackage, setPersonalPackage] = useState<Package | null>(null);
  const [familyPackage, setFamilyPackage] = useState<Package | null>(null);
  const [effectivePackage, setEffectivePackage] = useState<Package | null>(null);
  const [currentPackage, setCurrentPackage] = useState<Package | null>(null);
  const [activeFamilyId, setActiveFamilyIdState] = useState<string | null | undefined>(() => readStoredActiveFamilyId());
  const [familySubscriptionStatus, setFamilySubscriptionStatus] = useState<string | null>(null);
  const [isFamilyContext, setIsFamilyContext] = useState(false);
  const [usedLegacyFallback, setUsedLegacyFallback] = useState(false);
  const [familyId, setFamilyId] = useState<string | null>(null);
  const [familySubscriptionId, setFamilySubscriptionId] = useState<string | null>(null);
  const [lastEntitlement, setLastEntitlement] = useState<EffectiveEntitlementResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [familyLoading, setFamilyLoading] = useState(true);
  const [isActiveFamilyInitialized, setIsActiveFamilyInitialized] = useState(false);
  const currentPackageRequestVersion = useRef(0);
  const persistActiveFamilyId = useCallback((familyIdValue: string | null) => {
    setActiveFamilyIdState(familyIdValue);

    if (typeof window === "undefined") {
      return;
    }

    window.sessionStorage.setItem(ACTIVE_FAMILY_STORAGE_KEY, familyIdValue ?? "");
  }, []);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await get(Routes.GET_PACKAGES);
      if (Array.isArray(response)) {
        setPackages(response);
      } else if (response.success && response.packages) {
        setPackages(response.packages);
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.error("Packages fetch failed", error);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFamilies = useCallback(async () => {
    try {
      const response = await get(Routes.USER_FAMILIES);
      if (Array.isArray(response)) {
        setFamilies(
          response
            .map((family: FamilySummary) => ({
              idFamily: family?.idFamily,
              familyName: family?.familyName ?? null,
            }))
            .filter((family: FamilySummary) => Boolean(family.idFamily))
        );
      } else {
        setFamilies([]);
      }
    } catch (error) {
      console.error("Families fetch failed", error);
      setFamilies([]);
    } finally {
      setFamilyLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!profile) {
      setFamilies([]);
      setFamilyLoading(false);
      return;
    }

    setFamilyLoading(true);
    void fetchFamilies();
  }, [fetchFamilies, profile?.email]);

  useEffect(() => {
    if (isActiveFamilyInitialized || !profile || familyLoading) {
      return;
    }

    const storedFamilyId = activeFamilyId;
    const defaultFamilyId = families[0]?.idFamily ?? null;
    const resolvedFamilyId =
      storedFamilyId && families.some((family) => family.idFamily === storedFamilyId)
        ? storedFamilyId
        : defaultFamilyId;

    setActiveFamilyIdState(resolvedFamilyId);
    setIsActiveFamilyInitialized(true);
  }, [activeFamilyId, families, familyLoading, isActiveFamilyInitialized, profile]);

  useEffect(() => {
    if (!isActiveFamilyInitialized || !activeFamilyId) {
      return;
    }

    if (!families.some((family) => family.idFamily === activeFamilyId)) {
      setActiveFamilyIdState(families[0]?.idFamily ?? null);
    }
  }, [activeFamilyId, families, isActiveFamilyInitialized]);

  useEffect(() => {
    if (!isActiveFamilyInitialized || typeof window === "undefined" || activeFamilyId === undefined) {
      return;
    }

    window.sessionStorage.setItem(ACTIVE_FAMILY_STORAGE_KEY, activeFamilyId ?? "");
  }, [activeFamilyId, isActiveFamilyInitialized]);

  const fetchUserPackage = useCallback(async () => {
    const requestVersion = ++currentPackageRequestVersion.current;

    try {
      const selectedFamilyId = activeFamilyId ?? "";
      const route = selectedFamilyId
        ? `${Routes.USER_EFFECTIVE_PACKAGE}?familyId=${encodeURIComponent(selectedFamilyId)}`
        : Routes.USER_EFFECTIVE_PACKAGE;
      const response = await get<EffectiveEntitlementResponse>(route);
      if (requestVersion !== currentPackageRequestVersion.current) {
        return;
      }

      const entitlement = response && !Array.isArray(response) ? response : null;
      setLastEntitlement(entitlement);
      setPersonalPackage(entitlement?.personalPackage ?? null);
      setFamilyPackage(entitlement?.familyPackage ?? null);
      setEffectivePackage(entitlement?.effectivePackage ?? null);
      setCurrentPackage(
        entitlement?.effectivePackage ?? entitlement?.personalPackage ?? null
      );
      setFamilySubscriptionStatus(entitlement?.familySubscriptionStatus ?? null);
      setIsFamilyContext(entitlement?.isFamilyContext ?? false);
      setUsedLegacyFallback(entitlement?.usedLegacyFallback ?? false);
      setFamilyId(entitlement?.familyId ?? null);
      setFamilySubscriptionId(entitlement?.familySubscriptionId ?? null);
    } catch (error) {
      console.error("User effective entitlement fetch failed", error);

      try {
        const fallbackResponse = await get(Routes.USER_PACKAGES);
        if (requestVersion !== currentPackageRequestVersion.current) {
          return;
        }

        const fallbackPackage =
          fallbackResponse && fallbackResponse.success
            ? fallbackResponse.package || null
            : fallbackResponse && !Array.isArray(fallbackResponse)
              ? fallbackResponse || null
              : null;

        setLastEntitlement(null);
        setPersonalPackage(fallbackPackage);
        setFamilyPackage(null);
        setEffectivePackage(fallbackPackage);
        setCurrentPackage(fallbackPackage);
        setFamilySubscriptionStatus(null);
        setIsFamilyContext(false);
        setUsedLegacyFallback(false);
        setFamilyId(null);
        setFamilySubscriptionId(null);
      } catch (fallbackError) {
        console.error("User package fallback fetch failed", fallbackError);
        if (requestVersion === currentPackageRequestVersion.current) {
          setLastEntitlement(null);
          setPersonalPackage(null);
          setFamilyPackage(null);
          setEffectivePackage(null);
          setCurrentPackage(null);
          setFamilySubscriptionStatus(null);
          setIsFamilyContext(false);
          setUsedLegacyFallback(false);
          setFamilyId(null);
          setFamilySubscriptionId(null);
        }
      }
    }
  }, [activeFamilyId, packages]);

  const activeFamilyName = useMemo(() => {
    if (!activeFamilyId) {
      return null;
    }

    return families.find((family) => family.idFamily === activeFamilyId)?.familyName ?? null;
  }, [activeFamilyId, families]);

  useEffect(() => {
    void fetchPackages();
  }, [fetchPackages]);

  useEffect(() => {
    if (!isActiveFamilyInitialized) {
      return;
    }

    void fetchUserPackage();
  }, [fetchUserPackage, isActiveFamilyInitialized]);

  return (
    <PackageContext.Provider value={{
      packages,
      families,
      personalPackage,
      familyPackage,
      effectivePackage,
      currentPackage,
      activeFamilyId: activeFamilyId ?? null,
      activeFamilyName,
      familySubscriptionStatus,
      isFamilyContext,
      usedLegacyFallback,
      familyId,
      familySubscriptionId,
      loading,
      familyLoading,
      refreshPackage: fetchUserPackage,
      refreshFamilies: fetchFamilies,
      setActiveFamilyId: persistActiveFamilyId,
      lastEntitlement
    }}>
      {children}
    </PackageContext.Provider>
  );
};
